export const story = {
  title: "The Boy Who Asked Why",
  prologue: `The sky stretched infinitely above, a vast ocean of darkness dotted with shimmering specks of light. To most, they were just stars—mere pinpricks in the fabric of the night. But to Vedant Sharma, they were something more.

They were questions.

Each star held a secret, a mystery waiting to be unraveled. Each constellation whispered of ancient stories, forgotten myths, and worlds beyond human reach.

From the time he was a child, Vedant had been drawn to the sky. He would lie on his terrace, arms folded behind his head, staring up at the celestial expanse with an insatiable curiosity. He wasn't content with simply looking—he wanted to understand.

Why do the stars shine?
Why does the moon change shape?
Why can't we reach the places we dream of?

His questions never ended. They expanded with time, stretching wider than the sky itself.

Vedant had always believed that if he looked hard enough, he could find the answers he sought.

But some answers weren't written in books.

Some were hidden in places he had never thought to search.

And one day, the universe would bring him face to face with a mystery far greater than the stars—a mystery named Avni.

But before the cosmos wove their destinies together, before their paths crossed under the same night sky, Vedant had to first embark on the journey that had always defined him.

The journey of asking why.`,
  chapters: [
    {
      title: "Chapter One: The Boy Who Asked Why",
      content: "",
      subChapters: [
        {
          title: "The Questions That Never Stopped",
          content: `The first thing anyone noticed about Vedant Sharma as a child was the way his eyes never stayed still. They weren't restless—just… searching. Observing. As if the world was a puzzle, and if he stared long enough, the pieces would fall into place.

His mother often joked, "Beta, your brain runs faster than your feet."

But Vedant didn't think it was a joke. His thoughts did run faster than his feet, sometimes so fast he couldn't keep up with them.

He was the boy who asked why.

"Why does the moon follow us when we walk?"
"Why do we have to sleep at night? Why can't we sleep in the morning and live at night like owls?"
"Why do people die? And where do they go after that?"
"Why do some people become rich and some people don't?"
"Why can't I touch the stars?"

The adults around him had different reactions to his endless curiosity. His mother, a gentle woman with a patient smile, tried her best to answer him. His father, who ran a small offset printing press, had little time for such questions—he was always busy meeting deadlines, running machines, and managing workers.

But it was his Dadaji (grandfather) who truly encouraged his curiosity.

Vedant's Dadaji had been a teacher once, in a time long before Vedant was born. Now, retired, he spent most of his days reading newspapers and drinking chai on the balcony. He was a quiet man, but when Vedant asked a question, he never brushed it aside.

Instead, he would smile, adjust his thick glasses, and say, "Let's find out."

That was how it started—how Vedant fell in love with searching for answers.

One afternoon, when he was six, he sat on Dadaji's lap, flipping through an old book. And there, for the first time, he saw it.

A full-page image of the Milky Way galaxy. Swirls of blue and white, stars packed together like dust. A universe stretching far beyond anything he had ever imagined.

Something in his chest stirred.

"What is this?" he whispered.

Dadaji chuckled. "The cosmos."

The word sounded powerful. Endless.

From that moment, Vedant wasn't just a boy who asked "why."
He was a boy who wanted to find out.`
        },
        {
          title: "The Curious Explorer",
          content: `Curiosity became Vedant's second skin.

His world was no longer limited to his home, school, and the small printing press where his father worked. He started exploring everything.

He read encyclopedias meant for older kids, flipping past pages of history and geography just to find the ones about space.

He sneaked into the library at school, picking up books about the universe, time, and black holes—even if he didn't understand half of what they said.

He conducted small experiments—melting ice cubes under different lamps to see which one was hottest, trying to make a compass with a magnet and a needle, and even once, trying to launch a homemade rocket using an empty plastic bottle and baking soda (it failed spectacularly, but he still called it a "successful experiment").

He stared at people the same way he stared at the sky—watching, observing, trying to understand.

But the more he observed, the more he realized something.

People were not the same.`
        },
        {
          title: "The First Realization",
          content: `Between the ages of four and eight, Vedant believed something very simple:

Everyone is good. Everyone thinks the same. Everyone dreams the same.

His little mind assumed that all people wanted to explore, all people wanted to find answers, and all people had the same questions as him.

But school proved him wrong.

At first, he was excited. School meant more books, more knowledge, more people who might share his love for discovering the unknown.

But he soon realized that not everyone cared about the same things he did.

Some kids didn't care about the stars. They cared about cricket, cartoons, or trading WWE cards.

Some kids didn't like asking questions. They liked giving answers, memorizing them, and repeating them to score marks.

Some kids thought curiosity was annoying.

The first time Vedant asked, "Do you think time is real, or is it just something humans made up?", the class fell silent.

Then someone laughed.

Then another.

Then the teacher said, "Vedant, stop daydreaming and focus on the syllabus."

That day, he realized something strange.

Asking why was not something everyone liked.`
        },
        {
          title: "The Bright Student With No Friends",
          content: `Vedant was a good student.

His notebooks were always neat. His handwriting was perfect. He did well in exams.

But he was not popular.

He had classmates, but not friends.

He was too serious, too disciplined, too obsessed with things no one else cared about. Other kids found him strange, different.

Some ignored him.
Some teased him.
Some were nice, but they never truly understood him.

He didn't mind. Or at least, he told himself he didn't.

Because at the end of the day, when school ended and he climbed up to his terrace, the stars were still there.

And the stars? They didn't laugh at him.

They just watched.`
        },
        {
          title: "The Stars That Changed Everything",
          content: `By the time he was ten, Vedant had built a small "space corner" in his house.

It wasn't much. But to Vedant, it was everything.

He felt like an explorer, even if the places he explored were just in his mind.

But deep down, a small part of him wished he had someone to share it with.

Someone who looked at the sky the same way he did.

Someone who would ask, "Do you think time is real?" and not laugh at him.

He didn't know it yet.
But that person was out there.

Somewhere.

Waiting.`
        }
      ]
    }
  ]
};